import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import { ArrowLeft, Pen, Eraser, Type, Undo2, Trash2, Share2, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Avatar } from "../components/ui-bits";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../lib/utils";

const W = 1600;
const H = 1000;
const COLORS = ["#1B2A6B", "#E31E24", "#0E7490", "#B45309", "#047857", "#000000"];
const SIZES = [{ v: 2, label: "S" }, { v: 4, label: "M" }, { v: 8, label: "L" }];

export default function WhiteboardEditor() {
  const { wbId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { users } = useData();
  const canvasRef = useRef(null);
  const [board, setBoard] = useState(null);
  const [name, setName] = useState("");
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#E31E24");
  const [size, setSize] = useState(4);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [textInput, setTextInput] = useState(null);
  const shareableUsers = useMemo(() => users.filter((u) => u.id !== board?.owner_id), [users, board?.owner_id]);
  const drawing = useRef(false);
  const last = useRef(null);
  const undoStack = useRef([]);
  const dirty = useRef(false);
  const saveTimer = useRef(null);

  const inited = useRef(false);

  // Deps intentionally limited: api/toast/formatApiError are module imports (non-reactive).
  useEffect(() => {
    api.get(`/whiteboards/${wbId}`).then(({ data }) => {
      setBoard(data);
      setName(data.name);
    }).catch((e) => {
      toast.error(formatApiError(e));
      navigate("/whiteboards");
    });
  }, [wbId, navigate]);

  // Deps intentionally limited to board: canvasRef/inited are stable refs and init must run once.
  useEffect(() => {
    if (!board || inited.current || !canvasRef.current) return;
    inited.current = true;
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    if (board.content) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, W, H);
      img.src = board.content;
    }
  }, [board]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (W / rect.width), y: (e.clientY - rect.top) * (H / rect.height) };
  };

  const pushUndo = () => {
    undoStack.current.push(canvasRef.current.toDataURL());
    if (undoStack.current.length > 30) undoStack.current.shift();
  };

  const scheduleSave = () => {
    dirty.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 1500);
  };

  const save = async () => {
    if (!dirty.current) return;
    dirty.current = false;
    setSaving(true);
    try {
      const content = canvasRef.current.toDataURL("image/png");
      await api.patch(`/whiteboards/${wbId}`, { content });
      setSavedAt(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const startDraw = (e) => {
    e.preventDefault();
    if (tool === "text") {
      setTextInput({ ...pos(e), value: "" });
      return;
    }
    pushUndo();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.lineWidth = tool === "eraser" ? size * 6 : size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    scheduleSave();
  };

  const endDraw = () => { drawing.current = false; };

  const undo = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    const ctx = canvasRef.current.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, H);
      ctx.drawImage(img, 0, 0, W, H);
      scheduleSave();
    };
    img.src = prev;
  };

  const clearAll = () => {
    if (!window.confirm("Clear the whole board?")) return;
    pushUndo();
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    scheduleSave();
  };

  const commitText = () => {
    if (textInput?.value.trim()) {
      pushUndo();
      const ctx = canvasRef.current.getContext("2d");
      ctx.fillStyle = color;
      ctx.font = `600 ${size * 6 + 10}px Manrope, sans-serif`;
      ctx.fillText(textInput.value.trim(), textInput.x, textInput.y);
      scheduleSave();
    }
    setTextInput(null);
  };

  const saveName = async () => {
    if (!name.trim() || name === board.name) return;
    try {
      const { data } = await api.patch(`/whiteboards/${wbId}`, { name: name.trim() });
      setBoard(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleShare = async (uid) => {
    const cur = board.shared_with || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    try {
      const { data } = await api.patch(`/whiteboards/${wbId}`, { shared_with: next });
      setBoard(data);
      toast.success("Sharing updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!board) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="wb-editor-loading">
        <div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const isOwner = user?.role === "admin" || board.owner_id === user?.id;
  const toolBtn = (t) => cn("h-8 w-8 flex items-center justify-center rounded-md transition-colors",
    tool === t ? "bg-[#E31E24] text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground");

  const saveLabel = () => {
    if (saving) return "Saving…";
    if (savedAt) return `Saved ${formatDistanceToNow(savedAt, { addSuffix: true })}`;
    return "Auto-save on";
  };

  return (
    <div className="h-full flex flex-col" data-testid="whiteboard-editor">
      <div className="h-12 border-b border-border flex items-center gap-2 px-3 shrink-0 bg-background overflow-x-auto" data-testid="wb-toolbar">
        <button data-testid="wb-back" onClick={() => { save(); navigate("/whiteboards"); }}
          className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
          <ArrowLeft size={16} />
        </button>
        <input data-testid="wb-name-input" value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()} disabled={!isOwner}
          className="font-heading font-bold text-sm bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded px-1.5 h-7 w-48" />

        <div className="flex items-center gap-1 ml-3 border-l border-border pl-3">
          <button data-testid="wb-tool-pen" className={toolBtn("pen")} onClick={() => setTool("pen")} title="Pen"><Pen size={15} /></button>
          <button data-testid="wb-tool-eraser" className={toolBtn("eraser")} onClick={() => setTool("eraser")} title="Eraser"><Eraser size={15} /></button>
          <button data-testid="wb-tool-text" className={toolBtn("text")} onClick={() => setTool("text")} title="Text"><Type size={15} /></button>
        </div>

        <div className="flex items-center gap-1.5 ml-2">
          {COLORS.map((c) => (
            <button key={c} data-testid={`wb-color-${c.replace("#", "")}`} onClick={() => { setColor(c); if (tool === "eraser") setTool("pen"); }}
              className={cn("h-5 w-5 rounded-full transition-transform", color === c && tool !== "eraser" && "ring-2 ring-offset-2 ring-foreground scale-110")}
              style={{ backgroundColor: c }} />
          ))}
        </div>

        <div className="flex items-center gap-1 ml-2">
          {SIZES.map((s) => (
            <button key={s.v} data-testid={`wb-size-${s.label}`} onClick={() => setSize(s.v)}
              className={cn("h-7 w-7 text-xs font-semibold rounded-md transition-colors", size === s.v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted")}>
              {s.label}
            </button>
          ))}
        </div>

        <button data-testid="wb-undo" onClick={undo} className="h-8 w-8 ml-1 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Undo">
          <Undo2 size={15} />
        </button>
        <button data-testid="wb-clear" onClick={clearAll} className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-[#E31E24]/10 hover:text-[#E31E24] transition-colors" title="Clear board">
          <Trash2 size={15} />
        </button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground" data-testid="wb-save-status">
            {saveLabel()}
          </span>
          {isOwner && (
            <Popover open={shareOpen} onOpenChange={setShareOpen}>
              <PopoverTrigger asChild>
                <button data-testid="wb-share-button" className="h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md border border-border hover:bg-muted transition-colors">
                  <Share2 size={14} />Share{(board.shared_with || []).length > 0 && ` · ${board.shared_with.length}`}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2" data-testid="wb-share-menu">
                <p className="text-xs font-semibold mb-1.5 px-1">Share with members</p>
                {shareableUsers.map((u) => (
                  <button key={u.id} data-testid={`wb-share-toggle-${u.id}`} onClick={() => toggleShare(u.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm">
                    <span className={cn("h-4 w-4 rounded border flex items-center justify-center", (board.shared_with || []).includes(u.id) ? "bg-[#E31E24] border-[#E31E24] text-white" : "border-border")}>
                      {(board.shared_with || []).includes(u.id) && <Check size={11} strokeWidth={3} />}
                    </span>
                    <Avatar name={u.name} userId={u.id} size="sm" online={u.online} />
                    <span className="truncate">{u.name}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
          <div className="flex items-center -space-x-1.5">
            {[board.owner_id, ...(board.shared_with || [])].slice(0, 4).map((uid) => {
              const u = users.find((x) => x.id === uid);
              return u ? <Avatar key={uid} name={u.name} userId={u.id} size="sm" className="ring-2 ring-background" online={u.online} /> : null;
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-muted/40 p-3 overflow-hidden">
        <div className="relative w-full h-full bg-white rounded-lg border border-border shadow-sm overflow-hidden">
          <canvas ref={canvasRef} width={W} height={H} data-testid="wb-canvas"
            className={cn("w-full h-full touch-none", tool === "text" ? "cursor-text" : "cursor-crosshair")}
            onPointerDown={startDraw} onPointerMove={move} onPointerUp={endDraw} onPointerLeave={endDraw} />
          {textInput && (
            <input autoFocus data-testid="wb-text-input" value={textInput.value}
              onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
              onBlur={commitText}
              onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") setTextInput(null); }}
              placeholder="Type and press Enter"
              className="absolute bg-white/90 border border-[#E31E24] rounded px-2 py-1 text-sm focus-visible:outline-none"
              style={{ left: `${(textInput.x / W) * 100}%`, top: `${(textInput.y / H) * 100}%` }} />
          )}
        </div>
      </div>
    </div>
  );
}
